import React, { useState, useEffect, useCallback } from 'react';
import { apiClient, getThumbnailUrl } from '../services/api';
import { Material, TypPolozky, MultimediaSoubor } from '../types';
import SablonaModal from './SablonaModal';

interface ObjektDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  objektId?: number;
  sekce: number; // 1 = Minerály/Kameny, 2 = Šperky
}

interface ObjektFormData {
  sklad_id: string;
  nazev_cz: string;
  nazev_en: string;
  nazev_de: string;
  popis_cz: string;
  popis_en: string;
  popis_de: string;
  cena: string;
  rozmery: string;
  vaha: string;
  pocet_ks_sklad: number;
  rezervovatelne: boolean;
  skryta: boolean;
  typ_polozky_id: string;
  material_ids: number[];
}

const ObjektDialog: React.FC<ObjektDialogProps> = ({ isOpen, onClose, onSuccess, objektId, sekce }) => {
  const [formData, setFormData] = useState<ObjektFormData>({
    sklad_id: '',
    nazev_cz: '',
    nazev_en: '',
    nazev_de: '',
    popis_cz: '',
    popis_en: '',
    popis_de: '',
    cena: '',
    rozmery: '',
    vaha: '',
    pocet_ks_sklad: 1,
    rezervovatelne: false,
    skryta: false,
    typ_polozky_id: '',
    material_ids: []
  });
  
  const [materialy, setMaterialy] = useState<Material[]>([]);
  const [typyPolozek, setTypyPolozek] = useState<TypPolozky[]>([]);
  const [existingMedia, setExistingMedia] = useState<MultimediaSoubor[]>([]);
  const [newMediaFiles, setNewMediaFiles] = useState<File[]>([]);
  const [mediaToDelete, setMediaToDelete] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'cz' | 'en' | 'de'>('cz');
  const [sablonaModalOpen, setSablonaModalOpen] = useState(false);
  const [storageData, setStorageData] = useState({
    typ: '',
    krabice: '',
    patro: '',
    pozice: '',
});

  const loadFormData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Načíst materiály a typy položek
      // Pro nový objekt načíst pouze typy pro danou sekci
      // Pro editaci načíst všechny typy (aby bylo možné změnit sekci)
      const [materialyData, typyData] = await Promise.all([
        apiClient.getMaterials(),
        objektId ? apiClient.getTypyPolozek() : apiClient.getTypyPolozek(sekce)
      ]);

      setMaterialy(materialyData);
      setTypyPolozek(typyData);

      // Pokud editujeme existující objekt
      if (objektId) {
        const objektDetail = await apiClient.getObjektDetail(objektId);

        // Nejprve vynulovat skladové pole - pokud se sklad_id z DB
        // nepodaří rozparsovat (špatný tvar), pole zůstanou prázdná
        // a uživatel bude muset skladovou pozici vyplnit ručně.
        setStorageData({ typ: '', krabice: '', patro: '', pozice: '' });
        parseStorageId(objektDetail.sklad_id || '');

        setFormData({
          sklad_id: objektDetail.sklad_id || '',
          nazev_cz: objektDetail.nazev_cz || '',
          nazev_en: objektDetail.nazev_en || '',
          nazev_de: objektDetail.nazev_de || '',
          popis_cz: objektDetail.popis_cz || '',
          popis_en: objektDetail.popis_en || '',
          popis_de: objektDetail.popis_de || '',
          cena: objektDetail.cena?.toString() || '',
          rozmery: objektDetail.rozmery || '',
          vaha: objektDetail.vaha || '',
          pocet_ks_sklad: (objektDetail.pocet_ks_sklad ?? 1).toString(),
          rezervovatelne: objektDetail.rezervovatelne || false,
          skryta: objektDetail.skryta || false,
          typ_polozky_id: objektDetail.typ_polozky_id?.toString() || '',
          material_ids: objektDetail.material_ids || []
        });

        setExistingMedia(objektDetail.multimedia || []);
      } else {
        // Reset pro nový objekt
        setFormData({
          sklad_id: '',
          nazev_cz: '',
          nazev_en: '',
          nazev_de: '',
          popis_cz: '',
          popis_en: '',
          popis_de: '',
          cena: '',
          rozmery: '',
          vaha: '',
          pocet_ks_sklad: 1,
          rezervovatelne: true,
          skryta: false,
          typ_polozky_id: typyData[0]?.id.toString() || '',
          material_ids: []
        });
        setStorageData({ typ: '', krabice: '', patro: '', pozice: '' });
        setExistingMedia([]);
      }
      
      setNewMediaFiles([]);
      setMediaToDelete([]);
    } catch (err: any) {
      setError('Chyba při načítání dat: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [objektId, sekce]);

  useEffect(() => {
    if (isOpen) {
      loadFormData();
    }
  }, [isOpen, loadFormData]);

  const handleInputChange = (field: keyof ObjektFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStorageChange = (field: keyof typeof storageData, value: string) => {
    setStorageData(prev => ({ ...prev, [field]: value }));
  };

  const handleMaterialToggle = (materialId: number) => {
    setFormData(prev => ({
      ...prev,
      material_ids: prev.material_ids.includes(materialId)
        ? prev.material_ids.filter(id => id !== materialId)
        : [...prev.material_ids, materialId]
    }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setNewMediaFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setNewMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleMarkMediaForDeletion = (mediaId: number) => {
    setMediaToDelete(prev => 
      prev.includes(mediaId) ? prev.filter(id => id !== mediaId) : [...prev, mediaId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (
        !storageData.typ ||
        !storageData.krabice ||
        !storageData.patro ||
        !storageData.pozice
    ) {
        alert("Vyplňte kompletní skladovou pozici.");
        return;
    }

    try {
      setLoading(true);
      setError(null);

      let createdOrUpdatedObjektId = objektId;

      if (objektId) {

        const skladId = buildStorageId(objektId);

        // Aktualizace existujícího objektu
        await apiClient.updateObjekt(objektId, {
          sklad_id: skladId,
          nazev_cz: formData.nazev_cz,
          nazev_en: formData.nazev_en,
          nazev_de: formData.nazev_de,
          popis_cz: formData.popis_cz,
          popis_en: formData.popis_en,
          popis_de: formData.popis_de,
          cena: formData.cena ? parseFloat(formData.cena) : undefined,
          rozmery: formData.rozmery || undefined,
          vaha: formData.vaha || undefined,
          pocet_ks_sklad: formData.pocet_ks_sklad ? formData.pocet_ks_sklad : 1,
          rezervovatelne: formData.rezervovatelne,
          skryta: formData.skryta,
          typ_polozky_id: parseInt(formData.typ_polozky_id),
          material_ids: formData.material_ids
        });
      } else {
        // Vytvoření nového objektu
        const skladId = buildStorageId();
        const response = await apiClient.createObjekt({
          sklad_id: skladId,
          nazev_cz: formData.nazev_cz,
          nazev_en: formData.nazev_en,
          nazev_de: formData.nazev_de,
          popis_cz: formData.popis_cz,
          popis_en: formData.popis_en,
          popis_de: formData.popis_de,
          cena: formData.cena ? parseFloat(formData.cena) : undefined,
          rozmery: formData.rozmery || undefined,
          vaha: formData.vaha || undefined,
          pocet_ks_sklad: formData.pocet_ks_sklad ? formData.pocet_ks_sklad : 1,
          rezervovatelne: formData.rezervovatelne,
          skryta: formData.skryta,
          typ_polozky_id: parseInt(formData.typ_polozky_id),
          material_ids: formData.material_ids,
          sekce
        });
        createdOrUpdatedObjektId = response.objekt.id;
      }

      // Smazat označená média
      for (const mediaId of mediaToDelete) {
        await apiClient.deleteMultimedia(mediaId);
      }

      // Nahrát nová média
      for (const file of newMediaFiles) {
        await apiClient.uploadMultimedia(createdOrUpdatedObjektId!, file);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError('Chyba při ukládání: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Formát sklad_id: TYP_KRABICE_PATRO_POZICE_DBID
  // Poslední (5.) část je přímo databázové id objektu.
  // Pokud sklad_id neodpovídá tomuto tvaru, funkce nic nenastaví
  // a vrátí false - volající pak nechá skladová pole prázdná.
  const parseStorageId = (storageId: string) => {
    const parts = storageId.split('_');

    if (parts.length !== 5)
        return false;

    const [typ, krabice, patro, pozice, dbId] = parts;

    if (!['S', 'L', 'BK', 'BF', 'Sp', 'O'].includes(typ))
        return false;

    if (!['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].includes(patro))
        return false;

    if (isNaN(Number(krabice)))
        return false;

    if (isNaN(Number(pozice)))
        return false;

    if (isNaN(Number(dbId)))
        return false;

    setStorageData({
        typ,
        krabice,
        patro,
        pozice
    });

    return true;
  };

  const buildStorageId = (objectId?: number) => {
    return [
        storageData.typ,
        storageData.krabice,
        storageData.patro,
        storageData.pozice,
        objectId ? String(objectId) : '[NEW]'
    ].join('_');
  };



  // Náhled skladového ID pro readonly pole ve formuláři.
  // U editace použije skutečné databázové id, u nového záznamu
  // ho ještě neznáme, takže se zobrazí jen zástupný text.
  const previewStorageId = () => {
    if (!storageData.typ || !storageData.krabice || !storageData.patro || !storageData.pozice) {
      return '';
    }

    const dbIdPart = objektId ? String(objektId) : '[NEW]';

    return [storageData.typ, storageData.krabice, storageData.patro, storageData.pozice, dbIdPart].join('_');
  };

  if (!isOpen) return null;

  return (
    <>
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <div style={styles.header}>
          <h2>{objektId ? 'Editovat objekt' : 'Přidat nový objekt'}</h2>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Jazykové záložky */}
          <div style={styles.tabs}>
            <button
              type="button"
              style={activeTab === 'cz' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('cz')}
            >
              🇨🇿 Čeština
            </button>
            <button
              type="button"
              style={activeTab === 'en' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('en')}
            >
              🇬🇧 English
            </button>
            <button
              type="button"
              style={activeTab === 'de' ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab('de')}
            >
              🇩🇪 Deutsch
            </button>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: '4px' }}>
              <button
                type="button"
                style={styles.sablonyButton}
                onClick={() => setSablonaModalOpen(true)}
                title="Vybrat šablonu popisu a aplikovat ji do všech jazykových verzí"
              >
                📋 Šablony
              </button>
            </div>
          </div>

          <div style={styles.scrollContent}>
            {/* Skladové ID - na začátku, před jazykovými záložkami */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Skladové ID *</label>

              Tyo: <select
                style={styles.select}
                value={storageData.typ}
                onChange={e => handleStorageChange('typ', e.target.value)}
                required
              >
                <option value=""></option>
                <option value="S">S - Surový kámen</option>
                <option value="L">L - Leštěný kámen</option>
                <option value="BK">BK - Broušený kabašon</option>
                <option value="BF">BF - Broušený fasetovaný</option>
                <option value="Sp">Sp - Šperk</option>
                <option value="O">O - Ostatní</option>
              </select>

              Číslo krabice: <input
                type="number"
                style={styles.input}
                value={storageData.krabice}
                onChange={e => handleStorageChange('krabice', e.target.value)}
                placeholder=""
                min={1}
                required
              />

              Patro: <select
                style={styles.select}
                value={storageData.patro}
                onChange={e => handleStorageChange('patro', e.target.value)}
                required
              >
                <option value=""></option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="D">D</option>
                <option value="E">E</option>
                <option value="F">F</option>
                <option value="G">G</option>
                <option value="H">H</option>
                <option value="I">D</option>
                <option value="J">J</option>
                <option value="K">K</option>
                <option value="L">L</option>
                <option value="M">M</option>
                <option value="N">N</option>
                <option value="O">O</option>
                <option value="P">P</option>
                <option value="Q">Q</option>
                <option value="R">R</option>
                <option value="S">S</option>
                <option value="T">T</option>
                <option value="U">U</option>
                <option value="V">V</option>
                <option value="W">W</option>
                <option value="X">X</option>
                <option value="Y">Y</option>
                <option value="Z">Z</option>
              </select>

              Pozice: <input
                type="number"
                style={styles.input}
                value={storageData.pozice}
                onChange={e => handleStorageChange('pozice', e.target.value)}
                placeholder=""
                min={1}
                required
              />

              <input
                type="text"
                style={{
                  ...styles.input,
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold'
                }}
                value={previewStorageId()}
                placeholder="Skladové ID se sestaví po vyplnění všech polí"
                readOnly
              />
            </div>

            {/* Jazykový obsah */}
            {activeTab === 'cz' && (
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Název (CZ) *</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.nazev_cz}
                    onChange={e => handleInputChange('nazev_cz', e.target.value)}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Popis (CZ)</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.popis_cz}
                    onChange={e => handleInputChange('popis_cz', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            )}

            {activeTab === 'en' && (
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name (EN)</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.nazev_en}
                    onChange={e => handleInputChange('nazev_en', e.target.value)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Description (EN)</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.popis_en}
                    onChange={e => handleInputChange('popis_en', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            )}

            {activeTab === 'de' && (
              <div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name (DE)</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={formData.nazev_de}
                    onChange={e => handleInputChange('nazev_de', e.target.value)}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Beschreibung (DE)</label>
                  <textarea
                    style={styles.textarea}
                    value={formData.popis_de}
                    onChange={e => handleInputChange('popis_de', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
            )}

            {/* Ostatní pole (mimo jazyk) */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Cena (Kč)</label>
              <input
                type="number"
                step="0.01"
                style={styles.input}
                value={formData.cena}
                onChange={e => handleInputChange('cena', e.target.value)}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Rozměry</label>
              <input
                type="text"
                style={styles.input}
                value={formData.rozmery}
                onChange={e => handleInputChange('rozmery', e.target.value)}
                placeholder="např. 10x5x3 cm"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Váha</label>
              <input
                type="text"
                style={styles.input}
                value={formData.vaha}
                onChange={e => handleInputChange('vaha', e.target.value)}
                placeholder="např. 150g"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Počet ks na skladě</label>
              <input
                type="number"
                min="0"
                style={styles.input}
                value={formData.pocet_ks_sklad}
                onChange={e => handleInputChange('pocet_ks_sklad', e.target.value)}
                placeholder="1"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.rezervovatelne}
                  onChange={e => setFormData(prev => ({ ...prev, rezervovatelne: e.target.checked }))}
                />
                <span style={{ marginLeft: '8px' }}>Rezervovatelné</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.skryta}
                  onChange={e => setFormData(prev => ({ ...prev, skryta: e.target.checked }))}
                />
                <span style={{ marginLeft: '8px' }}>Skrytá (nepřistupná veřejnosti)</span>
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Typ položky *</label>
              <select
                style={styles.select}
                value={formData.typ_polozky_id}
                onChange={e => handleInputChange('typ_polozky_id', e.target.value)}
                required
              >
                <option value="">Vyberte typ</option>
                {typyPolozek.map(typ => (
                  <option key={typ.id} value={typ.id}>
                    {typ.nazev || typ.jmeno}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Materiály</label>
              <div style={styles.checkboxGroup}>
                {materialy.map(material => (
                  <label key={material.id} style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.material_ids.includes(material.id)}
                      onChange={() => handleMaterialToggle(material.id)}
                    />
                    <span style={{ marginLeft: '8px' }}>{material.nazev}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Existující média */}
            {existingMedia.length > 0 && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Existující média</label>
                <div style={styles.mediaGrid}>
                  {existingMedia.map(media => (
                    <div key={media.id} style={styles.mediaItem}>
                      <img
                        src={getThumbnailUrl(media.url)}
                        alt="Media"
                        style={{
                          ...styles.mediaThumbnail,
                          opacity: mediaToDelete.includes(media.id) ? 0.3 : 1
                        }}
                      />
                      <button
                        type="button"
                        style={mediaToDelete.includes(media.id) ? styles.mediaButtonRestore : styles.mediaButtonDelete}
                        onClick={() => handleMarkMediaForDeletion(media.id)}
                      >
                        {mediaToDelete.includes(media.id) ? '↺ Obnovit' : '🗑️ Smazat'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nová média */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Přidat nová média (obrázky/videa)</label>
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileSelect}
                style={styles.fileInput}
              />
              {newMediaFiles.length > 0 && (
                <div style={styles.newFilesList}>
                  {newMediaFiles.map((file, index) => (
                    <div key={index} style={styles.newFileItem}>
                      <span>{file.name}</span>
                      <button
                        type="button"
                        style={styles.removeFileButton}
                        onClick={() => handleRemoveNewFile(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={styles.footer}>
            <button type="button" style={styles.cancelButton} onClick={onClose} disabled={loading}>
              Zrušit
            </button>
            <button type="submit" style={styles.submitButton} disabled={loading}>
              {loading ? 'Ukládám...' : objektId ? 'Uložit změny' : 'Vytvořit'}
            </button>
          </div>
        </form>
      </div>
    </div>

    <SablonaModal
      isOpen={sablonaModalOpen}
      onClose={() => setSablonaModalOpen(false)}
      currentPopis={{
        cz: formData.popis_cz,
        en: formData.popis_en,
        de: formData.popis_de,
      }}
      onApply={({ cz, en, de }) => {
        setFormData(prev => ({
          ...prev,
          popis_cz: cz,
          popis_en: en,
          popis_de: de,
        }));
      }}
    />
    </>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    overflow: 'hidden'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #ddd',
    padding: '0 20px'
  },
  tab: {
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#666',
    borderBottom: '2px solid transparent'
  },
  tabActive: {
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#7B68EE',
    fontWeight: 'bold' as const,
    borderBottom: '2px solid #7B68EE'
  },
  scrollContent: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '20px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold' as const,
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box' as const
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box' as const
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '14px'
  },
  mediaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '10px'
  },
  mediaItem: {
    position: 'relative' as const,
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  mediaThumbnail: {
    width: '100%',
    height: '150px',
    objectFit: 'cover' as const,
    display: 'block'
  },
  mediaButtonDelete: {
    position: 'absolute' as const,
    top: '5px',
    right: '5px',
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#ff4444',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  mediaButtonRestore: {
    position: 'absolute' as const,
    top: '5px',
    right: '5px',
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  newFilesList: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '5px'
  },
  newFileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    fontSize: '14px'
  },
  removeFileButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#ff4444'
  },
  footer: {
    padding: '20px',
    borderTop: '1px solid #ddd',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    backgroundColor: '#7B68EE',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  error: {
    margin: '0 20px',
    padding: '15px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    fontSize: '14px'
  },
  sablonyButton: {
    padding: '6px 14px',
    fontSize: '13px',
    backgroundColor: '#7B68EE',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  }
};

export default ObjektDialog;
